/* stylelint-disable declaration-no-important */
import { ButtonBase } from '@mui/material';
import { styled, css } from '@mui/material/styles';
import { Scrollbar } from 'react-scrollbars-custom';

import { palette, px, rem, Theme } from '../../theme';
import { Icon } from '../Icon';

import {
    ScrollDirection,
    ScrollDirectionProperties,
} from './SimpleCarousel.types';

export const StyledCarouselContainer = styled('div')`
    ${({ theme, $isNewDesign }) => `
        position: relative;
        display: flex;
        justify-content: center;
        padding: ${$isNewDesign ? theme.tggSpacing([rem(1), 0, rem(2)], [2.5, 0, 5]) : theme.tggSpacing([0, 0, rem(2)], [0, 0, 5])};
    `};
`;

export const CarouselBase = styled('div')<ScrollDirectionProperties>`
    position: relative;
    box-sizing: border-box;
    display: flex;
    overflow: scroll;
    list-style: none;
    scrollbar-width: none;
    mask-image: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

export const getMaskImage = (direction: ScrollDirection) => {
    switch (direction) {
        case ScrollDirection.Left: {
            return 'linear-gradient(90deg, transparent 0%, black 10%)';
        }
        case ScrollDirection.Right: {
            return 'linear-gradient(90deg, black 90%, transparent 100%)';
        }
        case ScrollDirection.Both: {
            return 'linear-gradient(90deg, transparent 0%, black 10%, black 90%, transparent 100%)';
        }
        default: {
            return 'none';
        }
    }
};

export const StyledCarousel = styled(CarouselBase, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})`
    ${({ theme, $direction, $isNewDesign }) => `
        width: ${$isNewDesign ? '100%' : theme.tggSpacing('auto')};
        ${theme.breakpoints.up('desktop')} {
            margin-inline: ${$isNewDesign ? theme.tggSpacing(rem(8), 20) : theme.tggSpacing(0, 0)};
            mask-image: ${$isNewDesign ? 'none' : getMaskImage($direction)};
        }
    `}
`;

export const StyledSlide = styled('div')`
    flex: ${({ $areSlidesSameWidth }) => $areSlidesSameWidth && '1'};
    ${({ theme, $isLastSlide, $isNewDesign }) => css`
        padding: ${$isLastSlide
            ? theme.tggSpacing([0, 0, 0, rem(1)], [0, 0, 0, 2.5])
            : theme.tggSpacing([0, rem(1)], [0, 2.5])};
        &:first-of-type {
            padding-left: 0;
        }

        ${$isNewDesign &&
        `
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%;
            min-height: ${theme.tggSpacing(0, 0)};

            & > * {
                width: 100%;
            }

            & .promoCardBody {
                flex: 1;
                overflow: auto;
            }
        `}
    `};
`;

export const StyledSlides = styled('div')`
    ${({ theme, $isNewDesign }) => css`
        display: flex;
        flex-grow: 1;
        align-items: stretch;
        padding: ${theme.tggSpacing(
            [rem(1.5), rem(3), rem(1)],
            [3.75, 7.5, 2.5],
        )};
        ${theme.breakpoints.up('desktop')} {
            padding: ${theme.tggSpacing(
                [rem(1.5), rem(8), rem(1)],
                [6, 32, 4],
            )};
        }

        ${$isNewDesign &&
        `
            justify-content: center;
            margin: ${theme.tggSpacing([0, 'auto'])};
            ${theme.breakpoints.up('desktop')} {
                padding: ${theme.tggSpacing([rem(1.5), 0, rem(1)], [3.75, 0, 2.5])};
            }
        `}
    `};
`;

export const StyledIcon = styled(Icon)`
    && {
        cursor: pointer;
    }
`;

const StyledArrow = styled(ButtonBase)<ScrollDirectionProperties>`
    ${({ theme }: { theme: Theme }) => `
        display: none;
        cursor: pointer;
        background-color: ${theme.palette.common.white};
        border: ${theme.tggSpacing(rem(0.25), 0.5)} solid ${theme.getRebrandingColor(theme.palette.grey[900], theme.palette.tgg.surface.brand)};
        border-radius: 100%;
        &:focus-visible {
            outline: ${theme.tggSpacing(rem(0.2), 0.5)} solid
                ${theme.palette.primary.dark};
        }
        g {
            stroke: ${theme.getRebrandingColor('', theme.palette.tgg.surface.brand)}
        }
        ${theme.breakpoints.up('desktop')} {
            display: flex;
            align-content: center;
            justify-content: center;
            align-items: center;
        }
        path {
            stroke-width:${theme.tggSpacing('', 0)};
            fill: ${theme.getRebrandingColor(palette.energyBlue[500], theme.palette.tgg.surface.brand)};
            width: 100%;
            height: 100%;
            padding:${theme.tggSpacing(3, 3)};
        }
    `};
`;

export const StyledArrowLeft = styled(StyledArrow, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})`
    ${({
        theme,
        $direction,
    }: {
        theme: Theme;
        $direction: ScrollDirection;
    }) => `
        position: absolute;
        left: ${theme.tggSpacing(rem(1), 2.5)};
        width: ${theme.tggSpacing(rem(4.8), 12)};
        height: ${theme.tggSpacing(rem(4.8), 12)};
    `};

    visibility: ${({ $direction }: { $direction: ScrollDirection }) => {
        switch ($direction) {
            case ScrollDirection.Left:
            case ScrollDirection.Both: {
                return 'visible';
            }
            case ScrollDirection.Right:
            case ScrollDirection.None:
            default: {
                return 'hidden';
            }
        }
    }};
`;

export const StyledArrowRight = styled(StyledArrow, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})`
    ${({ theme }: { theme: Theme }) => `
        position: absolute;
        right: ${theme.tggSpacing(rem(1), 2.5)};
        width: ${theme.tggSpacing(rem(4.8), 12)};
        height: ${theme.tggSpacing(rem(4.8), 12)};
    `};
    visibility: ${({ $direction }: { $direction: ScrollDirection }) => {
        switch ($direction) {
            case ScrollDirection.Right:
            case ScrollDirection.Both: {
                return 'visible';
            }
            case ScrollDirection.Left:
            case ScrollDirection.None:
            default: {
                return 'hidden';
            }
        }
    }};
`;

export const StyledInnerArrowContainer = styled('div')`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
`;

export const StyledArrowContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        position: absolute;
        top: 50%;
        right: ${theme.tggSpacing(0, 0)};
        left: ${theme.tggSpacing(0, 0)};
        display: flex;
        justify-content: space-between;
        width: 100%;
        padding: ${theme.tggSpacing([0, rem(1)], [0, 2.5])};
        transform: translateY(${theme.tggSpacing(rem(-1), -2.5)});
    `};
`;

export const StyledInnerScrollContainer = styled('div')<{ $width?: any }>`
    ${({ theme, $width }: { theme: Theme; $width: any }) => `
        width: ${$width ? `${theme.tggSpacing(px($width), $width / 4)}` : '100%'};
        height: ${theme.tggSpacing(rem(0.4), 1)};
    `};
`;

export const ScrollbarContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        position: absolute;
        bottom: ${theme.tggSpacing(rem(0.4), 1)};
        display: flex;
        justify-content: center;
        width: 100%;
        height: ${theme.tggSpacing(rem(1), 2.5)};
        padding: ${theme.tggSpacing([0, rem(3)], [0, 7.5])};

        ${theme.breakpoints.up('mobile')} {
            left: 50%;
            width: ${theme.tggSpacing(rem(32), 80)};
            transform: translateX(-50%);
        }
    `};
`;

export const StyledScrollbar = styled(Scrollbar)`
    ${({ theme }: { theme: Theme }) => `
        .ScrollbarsCustom-TrackX {
            left: ${theme.tggSpacing(0, 0)} !important;
            width: 100% !important;
            height: ${theme.tggSpacing(rem(0.4), 1)} !important;
            cursor: pointer;
            background-color: ${theme.getRebrandingColor(theme.palette.grey[900], theme.palette.tgg.surface.container.main)} !important;
        }

        .ScrollbarsCustom-ThumbX {
            background-color: ${theme.getRebrandingColor(theme.palette.primary.main, theme.palette.tgg.surface.action.main)} !important;
        }
    `};
`;
